import api from './api'
import type { ApiResponse } from '@/types'
import type {
  WhatsAppAcceptCallResponse,
  WhatsAppActiveCall,
  WhatsAppLiveCall,
  WhatsAppOutboundCallResponse,
} from '@/lib/whatsapp-calls-types'

/**
 * REST client for WhatsApp (waxum) calls — all gated by
 * `feature:whatsapp_calls_enabled` plus the existing calls.place / calls.view /
 * calls.monitor permissions.
 */

export interface PlaceWhatsAppCallData {
  account_id: string
  /** Preferred: the backend resolves the number server-side (call-without-reveal). */
  lead_id?: string
  /** Only when the viewer can actually see the number. */
  to?: string
}

/**
 * Unwrap the standard `{success, data}` envelope while tolerating a bare body
 * (the calls endpoints return a small ad-hoc payload, not a Resource).
 */
function unwrap<T>(body: ApiResponse<T> | T): T {
  const envelope = body as ApiResponse<T>
  return envelope && typeof envelope === 'object' && 'data' in envelope
    ? envelope.data
    : (body as T)
}

/**
 * Place an outbound WhatsApp call. Returns the JID the media WS must dial —
 * the browser never sees or chooses the destination number itself.
 */
export const placeWhatsAppCall = async (
  data: PlaceWhatsAppCallData
): Promise<WhatsAppOutboundCallResponse> => {
  const response = await api.post<ApiResponse<WhatsAppOutboundCallResponse>>(
    '/whatsapp-calls/outbound',
    data
  )
  return unwrap(response.data)
}

/**
 * Claim a ringing call. 200 → this tab won and may attach audio (with the
 * returned `call_grant`); **409** → a colleague answered first (axios rejects;
 * callers inspect `error.response.status`).
 */
export const acceptWhatsAppCall = async (
  phoneCallId: string
): Promise<WhatsAppAcceptCallResponse> => {
  const response = await api.post<ApiResponse<WhatsAppAcceptCallResponse>>(
    `/whatsapp-calls/${phoneCallId}/accept`
  )
  return unwrap(response.data)
}

/** Explicitly decline. The row lands `missed` + `acknowledged` (no badge). */
export const rejectWhatsAppCall = async (phoneCallId: string): Promise<unknown> => {
  const response = await api.post<ApiResponse<unknown>>(`/whatsapp-calls/${phoneCallId}/reject`)
  return unwrap(response.data)
}

/**
 * The caller's OWN live call, if the sidecar still holds one (30 s grace after
 * the browser drops). Null when there is nothing to resume — including when the
 * feature is off, so this is safe to call unconditionally on app load.
 *
 * NOT unwrap(): successResponse(null) omits the `data` key entirely, and
 * unwrap's bare-body fallback would then hand back the `{success: true}`
 * envelope itself — a truthy object that made a ghost "call still live" card
 * appear on every login. Anything without a waxum_call_id is "nothing to
 * resume" (the resume flow can't re-attach without it anyway).
 */
export const getActiveWhatsAppCall = async (): Promise<WhatsAppActiveCall | null> => {
  const response = await api.get<ApiResponse<WhatsAppActiveCall | null>>('/whatsapp-calls/active')
  const call = unwrap(response.data)
  return call && typeof call === 'object' && 'waxum_call_id' in call ? call : null
}

/**
 * Every in-progress WhatsApp call in the clinic — the supervisor console's
 * list. Requires `calls.monitor`; 403 for everyone else (callers gate first).
 */
export const getLiveWhatsAppCalls = async (): Promise<WhatsAppLiveCall[]> => {
  const response = await api.get<ApiResponse<WhatsAppLiveCall[]>>('/whatsapp-calls/live')
  const data = unwrap(response.data)
  return Array.isArray(data) ? data : []
}
